// Base libs
import { useState, CompositionEvent, useEffect, useRef } from "react";
import { shallowEqual } from "react-redux";
import {
    GuxFormFieldNumber,
    GuxFormFieldTextarea,
    GuxTooltip,
} from "genesys-spark-components-react";

// General common
import { useAppDispatch, useAppSelector } from "app/hooks";
import FormType from "enums/FormType";
import { DefaultLimits } from "constants/DefaultLimits";

// Feature common
import { useStrings } from "features/i18n/useStrings";
import { DeleteButton, ReorderButtons } from "features/common/ActionIcons";
import { ReadOnlyRadio } from "features/common/ReadOnlyInput";
import { Card } from "features/common/Card";

// Feature slices
import { qualityFormActions } from "features/qualityForm/qualityFormSlice";
import {
    DependentIds,
    EntityType,
    initiateAnswerOptionDeletion,
} from "features/qualityForm/qualityFormSlice";
import {
    selectAnswerOption,
    selectFormType,
    selectQuestion,
    selectIsAnswerDefaultAnswer,
    selectIsAnyDefaultAnswerSelectedForQuestion,
} from "features/qualityForm/qualityFormSlices/index";

// Quality form commons
import { ErrorAction } from "../common/ErrorBox/VisibilityConflictErrorBox";
import { VisibilityConflictErrorBoxContainer } from "../common/ErrorBox/VisibilityConflictErrorBoxContainer";
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";
import { disableInputScroll } from "../common/DisableInputScroll";
import {
    invalidIntegerInputCharacters,
    selectIsAnswerOptionTextPresent,
    selectIsAnswerOptionTextUnderMaxLength,
    selectIsAnswerOptionValid,
} from "features/qualityForm/qualityFormValidations";
import {
    isAnswerOptionPointsEmpty,
    isAnswerOptionPointsInDecimalNumber,
    isAnswerOptionPointsOverLimits,
} from "features/qualityForm/qualityFormValidations/index";

// Locals
import { EvaluationAssistance } from "./EvaluationAssistance";
import { DefaultAnswerCheckbox } from "./DefaultAnswerCheckbox";

// Locales
import strings from "./Question.strings";

// Style
import "./AnswerOptionInput.less";

export function AnswerOption({
    answerOptionId,
    questionId,
    answerIndex,
    canDeleteAnswer,
}: {
    answerOptionId: string;
    questionId: string;
    answerIndex: number;
    canDeleteAnswer: boolean;
}) {
    const dispatch = useAppDispatch();
    const isAnswerValid = useAppSelector((state) =>
        selectIsAnswerOptionValid(state, answerOptionId)
    );
    const { canMoveUp, canMoveDown } = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        const answerCount = (question?.answerOptions ?? []).length;
        return {
            canMoveUp: answerIndex > 0,
            canMoveDown: answerIndex < answerCount - 1,
        };
    }, shallowEqual);

    return (
        <div className="AnswerOption" data-testid="answer-option">
            <ReorderButtons
                onMoveUp={
                    canMoveUp
                        ? () =>
                              dispatch(
                                  qualityFormActions.moveAnswer({
                                      answerOptionId: answerOptionId,
                                      newRelativePosition: -1,
                                      questionId: questionId,
                                      indexOfSelectedAnswerWithinQuestion:
                                          answerIndex,
                                  })
                              )
                        : undefined
                }
                onMoveDown={
                    canMoveDown
                        ? () =>
                              dispatch(
                                  qualityFormActions.moveAnswer({
                                      answerOptionId: answerOptionId,
                                      newRelativePosition: +1,
                                      questionId: questionId,
                                      indexOfSelectedAnswerWithinQuestion:
                                          answerIndex,
                                  })
                              )
                        : undefined
                }
            />
            <Card className={`${isAnswerValid ? "" : "error"}`}>
                <AnswerOptionInput
                    questionId={questionId}
                    answerOptionId={answerOptionId}
                    canDeleteAnswer={canDeleteAnswer}
                    answerIndex={answerIndex}
                />
            </Card>
        </div>
    );
}

export function AnswerOptionInput({
    questionId,
    answerOptionId,
    canDeleteAnswer,
    answerIndex,
}: {
    questionId: string;
    answerOptionId: string;
    canDeleteAnswer: boolean;
    answerIndex: number;
}) {
    const t = useStrings(strings);

    const answerOption = useAppSelector((state) =>
        selectAnswerOption(state, answerOptionId)
    );
    const dispatch = useAppDispatch();
    const [visibilityDependentIds, setVisibilityDependentIds] =
        useState<DependentIds>();
    const isOptionNamePresent = useAppSelector((state) =>
        selectIsAnswerOptionTextPresent(state, answerOptionId)
    );
    const formType = useAppSelector(selectFormType);
    const isOptionNameTooLong = useAppSelector(
        (state) =>
            !selectIsAnswerOptionTextUnderMaxLength(state, answerOptionId)
    );
    const answerPointsErrorMessage = useAppSelector((state) => {
        if (isAnswerOptionPointsEmpty(state, answerOptionId)) {
            return t.errors.emptyPoints;
        } else if (isAnswerOptionPointsInDecimalNumber(state, answerOptionId)) {
            return t.errors.decimalPoints;
        } else if (isAnswerOptionPointsOverLimits(state, answerOptionId)) {
            if (formType === FormType.EVALUATION) {
                return t.errors.decimalPoints;
            } else {
                return t.errors.answerPointsOverUpperLimit({
                    maxAnswerOptionPoints: DefaultLimits.maxAnswerOptionPoints,
                });
            }
        } else {
            return undefined;
        }
    });

    const numberInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => disableInputScroll(numberInputRef));

    const isDefaultAnswer = useAppSelector((state) =>
        selectIsAnswerDefaultAnswer(state, questionId, answerOptionId, false)
    );

    const isAnyDefaultAnswerSelectedForQuestion = useAppSelector((state) =>
        selectIsAnyDefaultAnswerSelectedForQuestion(state, questionId)
    );

    const isDefaultAnswerCheckboxDisabled =
        !isDefaultAnswer && isAnyDefaultAnswerSelectedForQuestion;
    return (
        <>
            <VisibilityConflictErrorBoxContainer
                visibilityDependentIds={visibilityDependentIds}
                clearVisibilityConflicts={() =>
                    setVisibilityDependentIds(undefined)
                }
                entityType={EntityType.Answer}
                errorAction={ErrorAction.Delete}
            >
                <div className="AnswerOptionInput">
                    <div className="answer-field">
                        <ReadOnlyRadio />
                        <GuxFormFieldTextarea
                            label-position="screenreader"
                            className="text-input"
                            resize="auto"
                        >
                            <textarea
                                slot="input"
                                name="questionName"
                                required
                                value={answerOption?.text}
                                onInput={({ currentTarget }) => {
                                    dispatch(
                                        qualityFormActions.changeAnswerText({
                                            text: currentTarget.value,
                                            answerOptionId: answerOptionId,
                                        })
                                    );
                                }}
                            ></textarea>
                            <label slot="label">
                                {t.labels.answerOptionTextInput}
                            </label>
                        </GuxFormFieldTextarea>
                    </div>
                    <GuxFormFieldNumber
                        className="points-input"
                        labelPosition="beside"
                    >
                        <input
                            slot="input"
                            type="number"
                            ref={numberInputRef}
                            required={true}
                            min="0"
                            step="1"
                            max={
                                formType === FormType.EVALUATION
                                    ? undefined
                                    : DefaultLimits.maxAnswerOptionPoints
                            }
                            value={answerOption?.value}
                            onBeforeInput={(
                                event: CompositionEvent<HTMLInputElement>
                            ) => {
                                if (
                                    invalidIntegerInputCharacters.includes(
                                        event.data
                                    )
                                ) {
                                    event.preventDefault();
                                }
                            }}
                            onChange={({ currentTarget }) => {
                                dispatch(
                                    qualityFormActions.changeAnswerValue({
                                        value: currentTarget.value,
                                        answerOptionId: answerOptionId,
                                    })
                                );
                            }}
                        ></input>
                        <label slot="label"> {t.labels.points} </label>
                    </GuxFormFieldNumber>
                    <div data-testid="delete-button-box">
                        <DeleteButton
                            onDelete={
                                canDeleteAnswer
                                    ? () => {
                                          const visibilityConflicts = dispatch(
                                              initiateAnswerOptionDeletion(
                                                  questionId,
                                                  answerOptionId
                                              )
                                          ) as unknown as DependentIds;
                                          if (
                                              visibilityDependentIds
                                                  ?.questionGroupIds.length !==
                                                  0 ||
                                              visibilityDependentIds
                                                  ?.questionIds.length !== 0
                                          ) {
                                              setVisibilityDependentIds(
                                                  visibilityConflicts
                                              );
                                          }
                                      }
                                    : undefined
                            }
                        />
                        {!canDeleteAnswer && (
                            <GuxTooltip>
                                {t.labels.cannotDeleteMCQOption({
                                    minAnswerOptionCount:
                                        DefaultLimits.minAnswerOptionCount,
                                })}
                            </GuxTooltip>
                        )}
                    </div>
                </div>
            </VisibilityConflictErrorBoxContainer>
            {(!isOptionNamePresent ||
                answerPointsErrorMessage ||
                isOptionNameTooLong) && (
                <div className="answer-option-error">
                    {!isOptionNamePresent && (
                        <ErrorStatement errorMessage={t.errors.requiredField} />
                    )}
                    {answerPointsErrorMessage && (
                        <ErrorStatement
                            errorMessage={answerPointsErrorMessage}
                        />
                    )}
                    {isOptionNameTooLong && (
                        <ErrorStatement
                            errorMessage={t.errors.answerOptionTextTooLong({
                                maxAnswerTextLength:
                                    DefaultLimits.maxAnswerTextLength,
                            })}
                        />
                    )}
                </div>
            )}
            <DefaultAnswerCheckbox
                questionId={questionId}
                optionId={answerOptionId}
                isMultipleSelect={false}
                isDefaultAnswer={isDefaultAnswer}
                isDisabled={isDefaultAnswerCheckboxDisabled}
                answerIndex={answerIndex}
            />
            <EvaluationAssistance
                questionId={questionId}
                answerId={answerOptionId}
                isMultipleSelect={false}
            />
        </>
    );
}
